import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Checkbox } from "../../components/ui/checkbox";
import { ChevronLeft, ChevronRight, Upload, FileText, Trash2, Download, CheckCircle2, Save, AlertTriangle } from "lucide-react";
import * as OPT from "../../lib/options";
import { Field, TextField, AreaField, SelectField, YesNo, CheckGroup, RatingRow } from "./FormControls";
import { API } from "../../lib/api";

const STEPS = ["Personal", "Eligibility", "Experience", "Market & Channels", "Commercial Skills", "Job Questions", "CV", "Review"];

export default function ApplicationWizard({ job }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState({ commercial_skills: {} });
  const [answers, setAnswers] = useState({});
  const [appId, setAppId] = useState(null);
  const [cv, setCv] = useState(null);
  const [consent, setConsent] = useState(false);
  const [futureConsent, setFutureConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, a, c] = await Promise.all([
        api.get("/candidate/profile"),
        api.post("/candidate/applications", { job_id: job?.id || null }),
        api.get("/candidate/cv"),
      ]);
      const prof = p.data || {};
      if (!prof.commercial_skills) prof.commercial_skills = {};
      if (!prof.first_name && user) prof.email = prof.email || user.email;
      setProfile(prof);
      setAppId(a.data.id);
      setAnswers(a.data.answers || {});
      if (a.data.status && a.data.status !== "draft") setSubmitted({ reference: a.data.reference });
      if (c.data && c.data.id) setCv(c.data);
    })().catch(() => toast.error("Could not load your application"));
  }, [user, job]);

  const upd = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const updSkill = (k, v) => setProfile((p) => ({ ...p, commercial_skills: { ...(p.commercial_skills || {}), [k]: v } }));
  const updAns = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const save = useCallback(async (silent) => {
    setSaving(true);
    try {
      await api.put("/candidate/profile", profile);
      if (appId) await api.put(`/candidate/applications/${appId}`, { answers });
      if (!silent) toast.success("Draft saved");
    } catch { if (!silent) toast.error("Save failed"); }
    finally { setSaving(false); }
  }, [profile, answers, appId]);

  const validateStep = () => {
    if (step === 0 && (!profile.first_name?.trim() || !profile.last_name?.trim())) { toast.error("Please enter your first and last name"); return false; }
    if (step === 1 && (!profile.country_residence || !profile.right_to_work || !profile.driving_licence || !profile.willing_travel)) { toast.error("Please complete the eligibility questions"); return false; }
    if (step === 5 && [1,2,3,4,5].some((n) => !(answers[`q${n}`] || "").trim())) { toast.error("Please answer all screening questions (1–5)"); return false; }
    return true;
  };
  const next = async () => { if (!validateStep()) return; await save(true); setStep((s) => Math.min(s + 1, STEPS.length - 1)); window.scrollTo(0, 0); };
  const prev = () => { setStep((s) => Math.max(s - 1, 0)); window.scrollTo(0, 0); };

  const submit = async () => {
    if (!consent) return toast.error("Please provide privacy consent to submit");
    try {
      await save(true);
      const { data } = await api.post(`/candidate/applications/${appId}/submit`, { consent, future_consent: futureConsent });
      setSubmitted({ reference: data.reference });
      window.scrollTo(0, 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
  };

  const uploadCv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    toast.loading("Uploading & parsing CV…", { id: "cv" });
    try {
      const { data } = await api.post("/candidate/cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCv(data);
      toast.success("CV uploaded", { id: "cv" });
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed", { id: "cv" }); }
  };

  const deleteCv = async () => { await api.delete("/candidate/cv"); setCv(null); toast.success("CV removed"); };

  if (submitted) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#101820] p-6">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-10 max-w-lg text-center border border-[#C79A3B]/40">
          <div className="w-16 h-16 rounded-full bg-[#2E7D32]/10 grid place-items-center mx-auto mb-5"><CheckCircle2 className="text-[#2E7D32] h-8 w-8" /></div>
          <h1 className="font-heading text-2xl font-extrabold text-[#101820]">Application submitted successfully</h1>
          <p className="mt-3 text-muted-foreground">Thank you for applying to KERA. Your application reference is:</p>
          <div className="mt-4 font-mono-data text-lg font-bold text-[#8a6a1e] bg-[#C79A3B]/10 border border-[#C79A3B]/30 rounded-lg py-3">{submitted.reference}</div>
          <Button data-testid="go-dashboard-button" onClick={() => navigate("/dashboard")} className="mt-7 bg-[#101820] text-[#FAF6EE] rounded-full px-7">Go to my dashboard</Button>
        </motion.div>
      </div>
    );
  }

  const pct = Math.round(((step + 1) / STEPS.length) * 100);
  const notIreland = profile.country_residence && profile.country_residence !== "Ireland";

  return (
    <div className="min-h-screen bg-[#F7F0E2]">
      <header className="bg-[#101820] text-[#FAF6EE] sticky top-0 z-40 border-b border-[#C79A3B]/25">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/kera-logo.webp" alt="KERA" className="h-8 w-auto" />
            <span className="font-heading font-bold hidden sm:inline">{job?.title || "Senior Sales Executive"} · Application</span>
          </Link>
          <Button data-testid="save-draft-button" variant="ghost" onClick={() => save(false)} disabled={saving}
            className="text-[#C79A3B] hover:text-[#D4AF57] hover:bg-white/5">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save draft"}
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>Step {step + 1} of {STEPS.length} · {STEPS[step]}</span><span>{pct}% complete</span>
          </div>
          <Progress data-testid="wizard-step-progress-bar" value={pct} className="h-2" />
          <div className="hidden md:flex justify-between mt-3">
            {STEPS.map((s, i) => (
              <button key={s} onClick={() => i <= step && setStep(i)} className={`text-[11px] font-medium ${i === step ? "text-[#8a6a1e]" : i < step ? "text-[#101820]/60" : "text-muted-foreground"}`}>{i + 1}. {s}</button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-xl border border-[#C79A3B]/30 p-6 md:p-8">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.25 }}>

              {step === 0 && (
                <Section title="Personal Details">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <TextField label="First name" required testid="wizard-input-first-name" value={profile.first_name} onChange={(v) => upd("first_name", v)} />
                    <TextField label="Last name" required testid="wizard-input-last-name" value={profile.last_name} onChange={(v) => upd("last_name", v)} />
                    <TextField label="Email" type="email" testid="wizard-input-email" value={profile.email} onChange={(v) => upd("email", v)} />
                    <TextField label="Phone" testid="wizard-input-phone" value={profile.phone} onChange={(v) => upd("phone", v)} />
                    <TextField label="Current town/city" testid="wizard-input-town" value={profile.town} onChange={(v) => upd("town", v)} />
                    <SelectField label="County" testid="wizard-input-county" options={OPT.COUNTIES} value={profile.county} onChange={(v) => upd("county", v)} />
                    <TextField label="Eircode (optional)" testid="wizard-input-eircode" value={profile.eircode} onChange={(v) => upd("eircode", v)} />
                  </div>
                </Section>
              )}

              {step === 1 && (
                <Section title="Work Eligibility">
                  <SelectField label="Current Country of Residence" required testid="wizard-input-country" options={OPT.COUNTRIES} value={profile.country_residence} onChange={(v) => upd("country_residence", v)} />
                  {notIreland && (
                    <div className="flex gap-3 bg-[#C76A32]/10 border border-[#C76A32]/30 rounded-lg p-4 text-sm text-[#101820]/80">
                      <AlertTriangle className="h-5 w-5 text-[#C76A32] shrink-0" />
                      <p>This vacancy is primarily intended for candidates currently based in Ireland. You may continue only if you have existing unrestricted permission to work in Ireland and can fulfil the field-travel requirements.</p>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-5">
                    <YesNo label="Do you currently have the right to work in Ireland without requiring employer sponsorship?" required testid="wizard-right-to-work" value={profile.right_to_work} onChange={(v) => upd("right_to_work", v)} />
                    <YesNo label="Are you currently resident in Ireland?" required testid="wizard-resident" value={profile.resident_ireland} onChange={(v) => upd("resident_ireland", v)} />
                    <SelectField label="Work authorisation type" testid="wizard-work-auth" options={OPT.WORK_AUTH} value={profile.work_auth_type} onChange={(v) => upd("work_auth_type", v)} />
                    <SelectField label="Driving licence" required testid="wizard-licence" options={OPT.LICENCES} value={profile.driving_licence} onChange={(v) => upd("driving_licence", v)} />
                    <YesNo label="Access to a vehicle?" testid="wizard-vehicle" value={profile.vehicle_access} onChange={(v) => upd("vehicle_access", v)} />
                    <YesNo label="Willing to travel nationwide throughout Ireland?" required testid="wizard-travel" value={profile.willing_travel} onChange={(v) => upd("willing_travel", v)} />
                  </div>
                  {profile.driving_licence && profile.driving_licence !== "No licence" && (
                    <div className="grid sm:grid-cols-2 gap-5 bg-[#FAF6EE] border border-[#C79A3B]/25 rounded-lg p-4">
                      <SelectField label="Licence category" required testid="wizard-licence-category" options={OPT.LICENCE_CATEGORIES} value={profile.licence_category} onChange={(v) => upd("licence_category", v)} />
                      <TextField label="Licence expiry date" type="date" required testid="wizard-licence-expiry" value={profile.licence_expiry} onChange={(v) => upd("licence_expiry", v)} />
                      <p className="sm:col-span-2 text-xs text-muted-foreground">Please confirm your full clean Irish/EU driving licence details — this is an essential requirement for this field-based role.</p>
                    </div>
                  )}
                  {profile.right_to_work === "no" && (
                    <p className="text-xs text-[#C76A32]">Your application will be flagged as "Eligibility Review Required". You will not be automatically rejected.</p>
                  )}
                </Section>
              )}

              {step === 2 && (
                <Section title="Professional Experience">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <TextField label="Current job title" testid="wizard-job-title" value={profile.current_job_title} onChange={(v) => upd("current_job_title", v)} />
                    <TextField label="Current employer" testid="wizard-employer" value={profile.current_employer} onChange={(v) => upd("current_employer", v)} />
                    <TextField label="Years of total sales experience" type="number" testid="wizard-years-total" value={profile.years_total_sales} onChange={(v) => upd("years_total_sales", v)} />
                    <TextField label="Years of field sales experience" type="number" testid="wizard-years-field" value={profile.years_field_sales} onChange={(v) => upd("years_field_sales", v)} />
                    <TextField label="Years of food/FMCG experience" type="number" testid="wizard-years-fmcg" value={profile.years_food_fmcg} onChange={(v) => upd("years_food_fmcg", v)} />
                    <TextField label="Years in ethnic food distribution" type="number" testid="wizard-years-ethnic" value={profile.years_ethnic_distribution} onChange={(v) => upd("years_ethnic_distribution", v)} />
                    <SelectField label="Current/most recent industry" testid="wizard-industry" options={OPT.INDUSTRIES} value={profile.current_industry} onChange={(v) => upd("current_industry", v)} />
                  </div>
                  <h3 className="font-heading font-semibold text-[#101820] mt-6 mb-1">Sales Performance</h3>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <SelectField label="Largest annual revenue portfolio managed" testid="wizard-revenue" options={OPT.REVENUE_BANDS} value={profile.revenue_portfolio} onChange={(v) => upd("revenue_portfolio", v)} />
                    <SelectField label="Number of active accounts managed" testid="wizard-accounts" options={OPT.ACCOUNT_BANDS} value={profile.active_accounts} onChange={(v) => upd("active_accounts", v)} />
                    <YesNo label="Have you personally opened new B2B accounts?" testid="wizard-opened" value={profile.opened_new_accounts} onChange={(v) => upd("opened_new_accounts", v)} />
                    <SelectField label="Approx. new accounts opened in past 24 months" testid="wizard-opened-band" options={OPT.OPENED_BANDS} value={profile.accounts_opened_24m} onChange={(v) => upd("accounts_opened_24m", v)} />
                  </div>
                  <CheckGroup label="Experience negotiating" testid="wizard-negotiation" options={OPT.NEGOTIATION} values={profile.negotiation_experience} onChange={(v) => upd("negotiation_experience", v)} />
                  <CheckGroup label="CRM systems used" testid="wizard-crm" options={OPT.CRM_SYSTEMS} values={profile.crm_systems} onChange={(v) => upd("crm_systems", v)} />
                  <h3 className="font-heading font-semibold text-[#101820] mt-6 mb-1">Education</h3>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <SelectField label="Highest qualification" required testid="wizard-qualification" options={OPT.QUALIFICATIONS} value={profile.highest_qualification} onChange={(v) => upd("highest_qualification", v)} />
                    <TextField label="Institution (optional)" testid="wizard-institution" value={profile.institution} onChange={(v) => upd("institution", v)} />
                    <TextField label="Course (optional)" testid="wizard-course" value={profile.course} onChange={(v) => upd("course", v)} />
                    <TextField label="Year completed (optional)" testid="wizard-year" value={profile.year_completed} onChange={(v) => upd("year_completed", v)} />
                  </div>
                </Section>
              )}

              {step === 3 && (
                <Section title="Market & Channel Experience">
                  <CheckGroup label="Channel experience" testid="wizard-channels" options={OPT.CHANNELS} values={profile.channels} onChange={(v) => upd("channels", v)} />
                  <YesNo label="Do you have direct experience selling within the Irish ethnic food market?" testid="wizard-irish-ethnic" value={profile.irish_ethnic_experience ? "yes" : profile.irish_ethnic_experience === false ? "no" : ""} onChange={(v) => upd("irish_ethnic_experience", v === "yes")} />
                  {profile.irish_ethnic_experience && (
                    <TextField label="Years of Irish ethnic food experience" type="number" testid="wizard-irish-ethnic-years" value={profile.irish_ethnic_years} onChange={(v) => upd("irish_ethnic_years", v)} />
                  )}
                  <CheckGroup label="Which categories?" testid="wizard-categories" options={OPT.CATEGORIES} values={profile.ethnic_categories} onChange={(v) => upd("ethnic_categories", v)} />
                  <CheckGroup label="Established business relationships in Ireland with:" testid="wizard-network" options={OPT.NETWORK} values={profile.network_relationships} onChange={(v) => upd("network_relationships", v)} />
                  <AreaField label="Briefly describe the type of customer network you have developed in Ireland." max={1500} testid="wizard-network-desc" value={profile.network_description} onChange={(v) => upd("network_description", v)} />
                  <p className="text-xs text-muted-foreground">Please do not include confidential customer lists.</p>
                </Section>
              )}

              {step === 4 && (
                <Section title="Commercial Skills">
                  <p className="text-sm text-muted-foreground mb-3">Rate your experience: 1 = None, 5 = Expert.</p>
                  <div className="bg-[#FAF6EE] rounded-lg border border-[#C79A3B]/20 px-4">
                    {OPT.SKILL_KEYS.map((s) => (
                      <RatingRow key={s.key} label={s.label} testid={`wizard-skill-${s.key}`} value={profile.commercial_skills?.[s.key]} onChange={(v) => updSkill(s.key, v)} />
                    ))}
                  </div>
                </Section>
              )}

              {step === 5 && (
                <Section title="Job-Specific Questions">
                  <AreaField label="1. Describe your experience selling food products within the Irish ethnic retail, wholesale or foodservice market." required max={2000} testid="wizard-q1" value={answers.q1} onChange={(v) => updAns("q1", v)} />
                  <AreaField label="2. Give an example of a new customer or territory you developed and the commercial result." required max={2000} testid="wizard-q2" value={answers.q2} onChange={(v) => updAns("q2", v)} />
                  <AreaField label="3. How would you grow KERA's distribution across Ireland over your first six months?" required max={2000} testid="wizard-q3" value={answers.q3} onChange={(v) => updAns("q3", v)} />
                  <AreaField label="4. Describe your experience working with distributors or wholesalers." required max={1500} testid="wizard-q4" value={answers.q4} onChange={(v) => updAns("q4", v)} />
                  <AreaField label="5. What relationships or market knowledge could you bring to KERA?" required max={1500} testid="wizard-q5" value={answers.q5} onChange={(v) => updAns("q5", v)} />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <TextField label="6. Expected salary (€)" testid="wizard-q6" value={answers.q6} onChange={(v) => updAns("q6", v)} placeholder="e.g. 55000 or 50-60K" />
                    <SelectField label="7. Notice period" testid="wizard-q7" options={OPT.NOTICE_PERIODS} value={answers.q7} onChange={(v) => updAns("q7", v)} />
                  </div>
                </Section>
              )}

              {step === 6 && (
                <Section title="CV / Resume Upload">
                  <p className="text-sm text-muted-foreground">Upload your CV as PDF, DOC or DOCX (max 10MB). We'll extract a summary — your entered answers are never overwritten.</p>
                  {!cv ? (
                    <label data-testid="wizard-input-cv-upload" className="mt-4 flex flex-col items-center justify-center border-2 border-dashed border-[#C79A3B]/40 rounded-xl py-12 cursor-pointer hover:border-[#C79A3B] hover:bg-[#C79A3B]/5 transition-all">
                      <Upload className="h-8 w-8 text-[#C79A3B] mb-2" />
                      <span className="text-[#101820] font-medium">Click to upload your CV</span>
                      <span className="text-xs text-muted-foreground mt-1">PDF, DOC, DOCX · up to 10MB</span>
                      <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => uploadCv(e.target.files[0])} />
                    </label>
                  ) : (
                    <div className="mt-4 bg-[#FAF6EE] border border-[#C79A3B]/30 rounded-xl p-5">
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-[#C79A3B]" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[#101820] truncate">{cv.original_filename}</div>
                          <div className="text-xs text-muted-foreground">{Math.round((cv.size || 0) / 1024)} KB</div>
                        </div>
                        <a href={`${API}/files/cv/${cv.id}/download?auth=${localStorage.getItem("kera_token")}`} target="_blank" rel="noreferrer" data-testid="cv-download-button">
                          <Button variant="outline" size="sm" className="border-[#C79A3B]/40"><Download className="h-4 w-4" /></Button>
                        </a>
                        <Button data-testid="cv-delete-button" variant="outline" size="sm" onClick={deleteCv} className="border-[#C76A32]/40 text-[#C76A32]"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                      {cv.parsed_summary && (
                        <div className="mt-4 text-sm">
                          <div className="text-xs uppercase tracking-wider text-[#8a6a1e] font-semibold mb-1">CV detected summary</div>
                          <p className="text-[#101820]/75">{cv.parsed_summary}</p>
                        </div>
                      )}
                      <label className="inline-block mt-4 text-sm text-[#8a6a1e] hover:underline cursor-pointer">
                        Replace file<input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => uploadCv(e.target.files[0])} />
                      </label>
                    </div>
                  )}
                </Section>
              )}

              {step === 7 && (
                <Section title="Review & Submit">
                  <ReviewBlock profile={profile} answers={answers} cv={cv} />
                  <div className="mt-6 space-y-3 bg-[#FAF6EE] rounded-lg border border-[#C79A3B]/25 p-4">
                    <label className="flex items-start gap-3 text-sm text-[#101820]/85">
                      <Checkbox data-testid="consent-checkbox" checked={consent} onCheckedChange={setConsent} className="mt-0.5" />
                      I consent to KERA processing the personal information provided in this application for recruitment purposes.
                    </label>
                    <label className="flex items-start gap-3 text-sm text-[#101820]/85">
                      <Checkbox data-testid="future-consent-checkbox" checked={futureConsent} onCheckedChange={setFutureConsent} className="mt-0.5" />
                      I agree that KERA may retain my profile for consideration for future suitable vacancies. (optional)
                    </label>
                  </div>
                  <Button data-testid="submit-application-button" onClick={submit} disabled={!consent}
                    className="mt-6 w-full bg-[#C79A3B] hover:bg-[#D4AF57] text-[#101820] font-bold rounded-full h-12 text-base disabled:opacity-50">
                    Submit application
                  </Button>
                </Section>
              )}

            </motion.div>
          </AnimatePresence>

          <div className="flex justify-between mt-8 pt-6 border-t border-[#C79A3B]/15">
            <Button data-testid="wizard-prev-button" variant="outline" onClick={prev} disabled={step === 0} className="border-[#C79A3B]/40 rounded-full">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 && (
              <Button data-testid="wizard-next-button" onClick={next} className="bg-[#101820] text-[#FAF6EE] rounded-full px-6">
                Save & Continue <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-5">
      <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#101820]">{title}</h2>
      {children}
    </div>
  );
}

function ReviewBlock({ profile, answers, cv }) {
  const row = (label, value) => (
    <div className="flex justify-between gap-4 py-1.5 border-b border-[#C79A3B]/10 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-[#101820] font-medium text-right">{value || "—"}</span>
    </div>
  );
  const list = (arr) => (arr && arr.length ? arr.join(", ") : "—");
  return (
    <div className="space-y-5">
      <Card title="Personal Details">
        {row("Name", `${profile.first_name || ""} ${profile.last_name || ""}`)}
        {row("Email", profile.email)}{row("Phone", profile.phone)}
        {row("Location", `${profile.town || ""} ${profile.county || ""}`)}{row("Country", profile.country_residence)}
      </Card>
      <Card title="Eligibility">
        {row("Resident in Ireland", profile.resident_ireland)}{row("Right to work (no sponsorship)", profile.right_to_work)}
        {row("Driving licence", profile.driving_licence)}{row("Willing to travel nationwide", profile.willing_travel)}
      </Card>
      <Card title="Professional Experience">
        {row("Current role", `${profile.current_job_title || ""} @ ${profile.current_employer || ""}`)}
        {row("Total sales years", profile.years_total_sales)}{row("Ethnic food distribution years", profile.years_ethnic_distribution)}
        {row("Industry", profile.current_industry)}
      </Card>
      <Card title="Market & Channels">
        {row("Channels", list(profile.channels))}{row("Categories", list(profile.ethnic_categories))}
        {row("Network", list(profile.network_relationships))}
      </Card>
      <Card title="Application Questions">
        {[1,2,3,4,5].map((n) => row(`Q${n}`, (answers[`q${n}`] || "").slice(0, 60) + ((answers[`q${n}`]||"").length > 60 ? "…" : "")))}
        {row("Expected salary", answers.q6)}{row("Notice period", answers.q7)}
      </Card>
      <Card title="CV">{row("Uploaded file", cv?.original_filename)}</Card>
    </div>
  );
}
function Card({ title, children }) {
  return (
    <div className="bg-white rounded-lg border border-[#C79A3B]/25 p-4">
      <h3 className="font-heading font-semibold text-[#101820] mb-2">{title}</h3>
      {children}
    </div>
  );
}
